import { useEffect, useState } from "react";
import { CheckCircle2, Download, Share, Smartphone } from "lucide-react";
import { Button, Card, useToast } from "@/ui";
import { useInstallPrompt } from "@/lib/useInstallPrompt";
import { useLanguage } from "@/i18n/language";
import { useTenant } from "@/tenant/TenantProvider";

/**
 * "Install app" as a page every role can reach.
 *
 * The header button only exists on screens that run through `AppShell`, and it
 * is hidden below `sm` — which is exactly the phone a karigar or counter is
 * holding. A page is reachable from every role's menu, on every screen size,
 * and has room to explain itself.
 *
 * It also carries the two cases the header button cannot express:
 *
 *   • iOS Safari never fires `beforeinstallprompt`. On an iPhone the button
 *     would simply never appear and there would be no way to install at all.
 *     iOS is detected and gets the Share → Add to Home Screen steps instead.
 *
 *   • A spent install offer. Chrome hands out one event and never repeats it;
 *     once `prompt()` has been called the same object silently no-ops. The page
 *     can say so and point at the browser menu, where a button cannot.
 */

/** iOS Safari, and only it. Chrome on iOS uses WebKit too but is not the target. */
function useIsIos(): boolean {
  const [ios] = useState(
    () =>
      typeof navigator !== "undefined" &&
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !/(CriOS|FxiOS|EdgiOS)/.test(navigator.userAgent),
  );
  return ios;
}

/** True once the app is running as an installed PWA rather than a browser tab. */
function useStandalone(): boolean {
  const [standalone] = useState(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        (window.navigator as { standalone?: boolean }).standalone === true),
  );
  return standalone;
}

export function InstallAppPage() {
  const { available, installed, prompt } = useInstallPrompt();
  const { t } = useLanguage();
  const { payload } = useTenant();
  const toast = useToast();
  const isIos = useIsIos();
  const standalone = useStandalone();
  const [busy, setBusy] = useState(false);

  // The early capture in `index.html` can arrive a beat after this mounts.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const appName = (payload.branding.appName as string) || "this app";
  const alreadyInstalled = installed || standalone;

  async function install(): Promise<void> {
    setBusy(true);
    const outcome = await prompt();
    setBusy(false);
    if (outcome === "accepted") {
      toast.success(t("ऐप इंस्टॉल हो गई।", "App installed."));
    } else if (outcome === "unavailable") {
      toast.error(
        t(
          "ब्राउज़र मेन्यू (⋮) से 'ऐप इंस्टॉल करें' चुनें।",
          "Use your browser menu (⋮) and choose “Install app”.",
        ),
      );
    }
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5">
      <div>
        <h1 className="font-title text-2xl font-semibold tracking-[-0.02em] text-ink">
          {t("ऐप इंस्टॉल करें", "Install app")}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {t(
            `${appName} को अपने फ़ोन की होम स्क्रीन पर रखें — ब्राउज़र खोलने की ज़रूरत नहीं।`,
            `Keep ${appName} on your phone's home screen — no browser needed.`,
          )}
        </p>
      </div>

      {alreadyInstalled ? (
        <Card className="flex items-start gap-3 p-5">
          <CheckCircle2 aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-success" />
          <div>
            <p className="text-sm font-medium text-ink">
              {t("पहले से इंस्टॉल है", "Already installed")}
            </p>
            <p className="mt-1 text-sm text-muted">
              {t(
                "यह ऐप आपके डिवाइस पर मौजूद है। होम स्क्रीन पर आइकन ढूँढें।",
                "This app is already on your device. Look for the icon on your home screen.",
              )}
            </p>
          </div>
        </Card>
      ) : (
        <>
          <Card className="flex items-start gap-3 p-5">
            <Smartphone aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-brand" />
            <div className="text-sm text-muted">
              <p className="font-medium text-ink">{t("क्यों इंस्टॉल करें?", "Why install?")}</p>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                <li>{t("एक टैप में खुलता है", "Opens in one tap")}</li>
                <li>{t("इंटरनेट धीमा हो तब भी काम करता है", "Works even on a slow connection")}</li>
                <li>{t("पूरी स्क्रीन पर दिखता है", "Fills the whole screen")}</li>
              </ul>
            </div>
          </Card>

          {/*
            Three states, and only one of them gets a button:

              available      — the browser is offering install right now
              iOS            — never offers; show the manual steps
              neither        — desktop without an offer, or an Android browser
                               that has not qualified yet

            Rendering a dead button in the last two is the failure mode this
            page exists to avoid.
          */}
          {mounted && available ? (
            <Card className="p-5">
              <Button
                block
                icon={<Download aria-hidden="true" className="size-4" />}
                loading={busy}
                onClick={() => void install()}
                size="lg"
                type="button"
              >
                {t("ऐप इंस्टॉल करें", "Install app")}
              </Button>
              <p className="mt-3 text-center text-xs text-muted">
                {t(
                  "अगला स्क्रीन पूछेगा — 'इंस्टॉल' दबाएँ।",
                  "The next screen will ask you to confirm — press Install.",
                )}
              </p>
            </Card>
          ) : null}

          {isIos ? (
            <Card className="p-5">
              <p className="text-sm font-medium text-ink">
                {t("iPhone पर इंस्टॉल करना", "Installing on an iPhone")}
              </p>
              <p className="mt-1 text-sm text-muted">
                {t(
                  "Safari में खुला है, इसलिए यह कदम हाथ से करना होगा:",
                  "You are in Safari, so this takes one step by hand:",
                )}
              </p>
              <ol className="mt-3 space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-semibold text-brand">
                    1
                  </span>
                  <span className="text-muted">
                    {t(
                      "नीचे का शेयर बटन दबाएँ ",
                      "Tap the Share button at the bottom ",
                    )}
                    <Share aria-hidden="true" className="inline size-4 align-text-top" />
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-semibold text-brand">
                    2
                  </span>
                  <span className="text-muted">
                    {t(
                      "मेन्यू में 'होम स्क्रीन पर जोड़ें' चुनें",
                      "Choose “Add to Home Screen” in the menu",
                    )}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-semibold text-brand">
                    3
                  </span>
                  <span className="text-muted">
                    {t("'जोड़ें' दबाएँ", "Tap Add")}
                  </span>
                </li>
              </ol>
            </Card>
          ) : null}

          {!available && !isIos ? (
            <Card className="p-5">
              <p className="text-sm font-medium text-ink">
                {t("आपका ब्राउज़र अभी इंस्टॉल नहीं करने दे रहा", "Your browser is not offering install yet")}
              </p>
              <p className="mt-1 text-sm text-muted">
                {t(
                  "ब्राउज़र मेन्यू (⋮) खोलें और 'ऐप इंस्टॉल करें' या 'होम स्क्रीन पर जोड़ें' चुनें। Chrome में यह विकल्प कुछ दिन बाद आ जाता है।",
                  "Open your browser menu (⋮) and choose “Install app” or “Add to Home screen”. Chrome offers this after you have visited a few times.",
                )}
              </p>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}
